/**
 * Astrally AI conversations — the storage layer behind "chats".
 *
 * The app used to keep ONE global thread per account at `allyHistory:<uid>`.
 * There was no way to start a fresh topic or return to an old one, so every
 * question the user ever asked lived in a single scroll that silently dropped
 * its oldest entries. This module turns that into many conversations:
 *
 *   allyChats:<uid>            → ChatMeta[]  (the index: title + times + count)
 *   allyChat:<uid>:<chatId>    → ChatEntry[] (one conversation's messages)
 *
 * Everything here is pure or takes its store as an argument, so the migration
 * from the old single thread can be tested without IndexedDB — see
 * allyChats.test.ts. AppState owns the React state; this file owns the shape.
 */

import { DEFAULT_ALLY_PERSONA, type AllyPersona } from '@astrally/shared';
import type { ChatEntry } from './AppState';

/** One row of the conversation index. Messages live under their own key. */
export interface ChatMeta {
  id: string;
  title: string;
  createdAtISO: string;
  updatedAtISO: string;
  messageCount: number;
  /**
   * Which astrologer this conversation is with.
   *
   * Per-chat rather than a global switch: a conversation has one voice, and
   * flipping personas mid-thread would leave replies that contradict their own
   * vocabulary. Absent on chats created before personas existed — they read as
   * the default, which is Jyotish, because that is what they were.
   */
  persona?: AllyPersona;
}

/**
 * Per-conversation cap. This is the SAME 80 the single thread had — it is now
 * 80 per conversation rather than 80 for a lifetime, which is the point.
 */
export const MAX_CHAT_ENTRIES = 80;

/** Placeholder until the first user message names the chat. */
export const DEFAULT_CHAT_TITLE = 'New chat';

/** Auto-titles are cut to roughly this, on a word boundary where there is one. */
export const TITLE_MAX = 40;

export function chatIndexKey(uid: string): string {
  return `allyChats:${uid}`;
}

export function chatKey(uid: string, chatId: string): string {
  return `allyChat:${uid}:${chatId}`;
}

/** The pre-multi-chat single thread. Read once, then migrated away. */
export function legacyHistoryKey(uid: string): string {
  return `allyHistory:${uid}`;
}

export function newChatId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * The narrow slice of localdb this module needs. Declared here so a test can
 * hand in a plain Map and so a later SQLite swap stays a drop-in.
 */
export interface ChatStore {
  get<T>(store: 'kv', key: string): Promise<T | undefined>;
  set<T>(store: 'kv', key: string, value: T): Promise<void>;
  del(store: 'kv', key: string): Promise<void>;
}

/** Squash whitespace and clamp to a title that fits one line of a list row. */
export function trimTitle(raw: string): string {
  const clean = raw.replace(/\s+/g, ' ').trim();
  if (!clean) return DEFAULT_CHAT_TITLE;
  if (clean.length <= TITLE_MAX) return clean;
  const cut = clean.slice(0, TITLE_MAX);
  const space = cut.lastIndexOf(' ');
  // Only honour a word boundary that is actually near the end — otherwise a
  // long first word would leave a two-character title.
  const body = space > TITLE_MAX - 14 ? cut.slice(0, space) : cut;
  return `${body.trimEnd()}…`;
}

/**
 * A chat is named after the first thing the USER said in it. A thread that
 * opens with an Ally line (a recommendation introduces itself, for instance)
 * falls back to that line so the row is never blank.
 */
export function titleFor(entries: ChatEntry[]): string {
  const first = entries.find((e) => e.role === 'user') ?? entries[0];
  return first ? trimTitle(first.text) : DEFAULT_CHAT_TITLE;
}

/** Newest conversation first — the order the list and the "resume" default use. */
export function sortChats(chats: ChatMeta[]): ChatMeta[] {
  return [...chats].sort((a, b) => b.updatedAtISO.localeCompare(a.updatedAtISO));
}

/**
 * Trust nothing that comes back off the device: a half-written index, an older
 * build's shape or a hand-edited store degrades to "fewer chats", never to a
 * crash on first render.
 */
export function sanitiseIndex(raw: unknown): ChatMeta[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: ChatMeta[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const c = item as Partial<ChatMeta>;
    if (typeof c.id !== 'string' || c.id.length === 0 || seen.has(c.id)) continue;
    seen.add(c.id);
    const created = typeof c.createdAtISO === 'string' ? c.createdAtISO : new Date(0).toISOString();
    out.push({
      id: c.id,
      title: typeof c.title === 'string' && c.title.trim() ? c.title : DEFAULT_CHAT_TITLE,
      createdAtISO: created,
      updatedAtISO: typeof c.updatedAtISO === 'string' ? c.updatedAtISO : created,
      messageCount:
        typeof c.messageCount === 'number' && c.messageCount > 0 ? Math.floor(c.messageCount) : 0,
      persona: c.persona === 'western' ? 'western' : DEFAULT_ALLY_PERSONA,
    });
  }
  return sortChats(out);
}

/** Same contract for the messages themselves. */
export function sanitiseEntries(raw: unknown): ChatEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ChatEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const e = item as Partial<ChatEntry>;
    if (typeof e.text !== 'string') continue;
    if (e.role !== 'user' && e.role !== 'ally') continue;
    out.push({
      ...(e as ChatEntry),
      ts: typeof e.ts === 'string' ? e.ts : new Date(0).toISOString(),
    });
  }
  return out;
}

/**
 * Recompute a chat's index row from its messages.
 *
 * The title is derived ONCE, while it is still the placeholder. After that it
 * belongs to the user: a rename must survive every later message, and a chat
 * must not silently rename itself when its first message ages out of the cap.
 */
export function metaForEntries(id: string, entries: ChatEntry[], prev?: ChatMeta): ChatMeta {
  const now = new Date().toISOString();
  const named = prev && prev.title !== DEFAULT_CHAT_TITLE;
  return {
    id,
    title: named ? prev!.title : titleFor(entries),
    createdAtISO: prev?.createdAtISO ?? entries[0]?.ts ?? now,
    updatedAtISO: entries[entries.length - 1]?.ts ?? prev?.updatedAtISO ?? now,
    messageCount: entries.length,
    persona: prev?.persona ?? DEFAULT_ALLY_PERSONA,
  };
}

/** Index after `chatId` was written with `entries`, still newest-first. */
export function touchChat(chats: ChatMeta[], chatId: string, entries: ChatEntry[]): ChatMeta[] {
  const prev = chats.find((c) => c.id === chatId);
  const meta = metaForEntries(chatId, entries, prev);
  return sortChats([meta, ...chats.filter((c) => c.id !== chatId)]);
}

export interface ChatSession {
  chats: ChatMeta[];
  activeChatId: string | null;
  /** Messages of the active chat, ready to render. */
  entries: ChatEntry[];
  /** True when this read converted the old single thread. Test hook. */
  migrated: boolean;
}

/**
 * In-flight reads, keyed by uid.
 *
 * React StrictMode runs the boot effect twice in development, and two
 * overlapping first reads would each find no index and each migrate — leaving
 * the user with their old thread duplicated into two chats. Sharing the
 * promise makes the migration happen exactly once per account. A failed read
 * clears the entry, so a retry is still a retry.
 */
const inFlight = new Map<string, Promise<ChatSession>>();

/**
 * Read the conversation index, MIGRATING the old single thread on first run.
 *
 * The migration is lazy (it happens on the first read that finds no index) and
 * ordered so a crash anywhere in it cannot lose messages: the conversation is
 * written first, the index second, and only once BOTH have landed is the old
 * `allyHistory:<uid>` key dropped. An interrupted run simply finds no index
 * next time and migrates again from the key that is still there.
 */
export function loadChatSession(db: ChatStore, uid: string): Promise<ChatSession> {
  const running = inFlight.get(uid);
  if (running) return running;
  const read = readChatSession(db, uid).finally(() => inFlight.delete(uid));
  inFlight.set(uid, read);
  return read;
}

async function readChatSession(db: ChatStore, uid: string): Promise<ChatSession> {
  const index = sanitiseIndex(await db.get<unknown>('kv', chatIndexKey(uid)).catch(() => undefined));
  if (index.length > 0) {
    const active = index[0];
    const entries = sanitiseEntries(
      await db.get<unknown>('kv', chatKey(uid, active.id)).catch(() => undefined),
    );
    return { chats: index, activeChatId: active.id, entries, migrated: false };
  }

  const legacy = sanitiseEntries(
    await db.get<unknown>('kv', legacyHistoryKey(uid)).catch(() => undefined),
  );
  if (legacy.length === 0) {
    return { chats: [], activeChatId: null, entries: [], migrated: false };
  }

  const id = newChatId();
  const entries = legacy.slice(-MAX_CHAT_ENTRIES);
  const meta = metaForEntries(id, entries);
  await db.set('kv', chatKey(uid, id), entries);
  await db.set('kv', chatIndexKey(uid), [meta]);
  await db.del('kv', legacyHistoryKey(uid)).catch(() => undefined);
  return { chats: [meta], activeChatId: id, entries, migrated: true };
}
